<template>
  <div class="w-full flex justify-center overflow-auto px-7 pt-12" style="height: calc(100vh - 136px); scrollbar-gutter: stable">
    <div class="w-full h-full rounded-t-3xl flex flex-col" style="max-width: 900px">
      <!-- Header -->
      <div class="space-y-1">
        <BaseText type="label" size="sm" class="text-text-muted">
          Lens Settings
        </BaseText>
        <BaseText size="sm" class="text-neutral-500">
          Adjust your company wide Lens settings including your data sources.
        </BaseText>
      </div>
      <div class="p-1 flex items-center relative bg-background-normal rounded-lg w-max mt-7">
        <!-- Total Lens Credits -->
        <div class="flex items-center gap-1 p-1">
          <LensIcon class="text-text-subdued" />
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ lensCounts.total }}
          </BaseText>
        </div>
        <!-- flex divider -->
        <div class="h-4 w-px bg-border-hover mx-1" />
        <div class="flex items-center gap-1.5 pl-2 pr-1.5 py-1">
          <BaseText size="sm" class="text-text-normal">
            Active Lens Brands
          </BaseText>
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ lensCounts.used }}
          </BaseText>
        </div>
        <!-- flex divider -->
        <div class="h-4 w-px bg-border-hover mx-1" />
        <div class="flex items-center gap-1.5 pl-2 pr-1.5 py-1">
          <BaseText size="sm" class="text-text-normal">
            Unused
          </BaseText>
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ lensCounts.open }}
          </BaseText>
        </div>
        <button class="base-btn py-1.5 px-2.5 h-full ml-1"
        @click="showLensEdit = true">
          <BaseText type="label" size="sm" class="text-text-muted">
            Get More
          </BaseText>
        </button>
      </div>
      <!-- Divider -->
      <div class="border-t border-border-normal w-full my-7" />
      <div class="space-y-1">
        <BaseText type="label" size="sm" class="text-text-muted">
          Data Sources
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          Authenticate one or multiple platform logins to provide access to ad accounts.
        </BaseText>
      </div>
      <!-- Platform Toggle -->
      <div class="flex items-center gap-0.5 p-0.5 rounded-lg bg-neutral-25 w-max overflow-hidden mt-7 flex-shrink-0">
        <button v-for="platform in platformOptions" :key="`toggle-${platform.name}`" class="platform-toggle-btn"
        :class="{'active': platform.isActive}" @click="platform.action">
          <component :is="platform.icon" :width="16" :height="16" />
          <BaseText type="label" size="sm" class="px-1">
            {{ platform.name }}
          </BaseText>
        </button>
      </div>
      <!-- PLATFORM ACCOUNTS LIST -->
      <!-- Loading -->
      <div v-if="loadingSources" class="flex items-center justify-start gap-3 flex-wrap mt-7 fade-in">
        <div v-for="i in 3" :key="i" class="w-full skeleton rounded-2xl" style="height: 56px" />
      </div>
      <!-- No Accounts -->
      <div v-else-if="!visiblePlatformAccounts.length">
        <!-- TODO -->
      </div>
      <!-- Accounts List -->
      <div v-else class="w-full flex flex-col gap-3 mt-7 fade-in pb-12" :key="`platform-accounts-${platformToggle}`">
        <DataSourceCard
          v-for="account in visiblePlatformAccounts"
          :key="`${platformToggle}-${account.id}`"
          :platform="platformToggle"
          :account="account"
          :init-expanded="visiblePlatformAccounts.length === 1"
          @refresh-ad-accounts="getTeamPlatformAccounts"
        />
        <!-- Add New Account Button -->
        <button class="add-more-btn w-full flex items-center gap-2 p-3 rounded-xl">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 6.25V10M10 10V13.75M10 10H6.25M10 10H13.75" stroke="#5E6678" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <BaseText type="label" size="sm" class="text-text-muted">
            Add New {{ formattedSelectedPlatformName }} Account
          </BaseText>
          <BaseText type="body" size="sm" class="text-text-normal">
            Connect another {{ formattedSelectedPlatformName }} account to Foreplay
          </BaseText>
        </button>
      </div>
      <!-- <div v-if="dataSources.facebook.length > 0 || loadingSources">
        <div class="flex items-center gap-2 mb-3 mt-7">
          <MetaPlatformIcon class="text-primary-blue" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Meta
          </BaseText>
        </div>
        <div v-if="loadingSources" class="flex items-center justify-start gap-3 flex-wrap">
          <div v-for="i in 3" :key="i" class="w-full skeleton rounded-2xl" style="height: 56px" />
        </div>
        <div v-else class="flex items-center justify-start gap-2 flex-wrap fade-in">
          <DataSourceCard
            v-for="dataSource in dataSources.facebook"
            :key="dataSource.connectionOwner"
            :data-source="dataSource"
            :is-owner="dataSource.connectionOwner === getUser.user_id"
            @editConnection="handleConnectFacebook(true)"
          />
        </div>
      </div> -->
    </div>
    <LensAccountModal
      v-if="showLensEdit"
      @close="showLensEdit = false"
    />
  </div>
</template>

<script>
import LensAPI from '@/api/lens'
import FirebaseAPI from '@/api/firebase'
import { mapGetters } from 'vuex'
import DataSourceCard from './lens/DataSourceCard.vue'
import LensAccountModal from './lens/LensAccountModal.vue'
// Icons
import LensIcon from '../globals/Icons/SidebarIcons/LensIcon.vue'
import MetaPlatformIcon from '../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'

const providerKeyMap = {
  'meta': 'facebook',
  'tiktok': 'tiktok'
}

export default {
  name: 'LensOverviewSettings',
  components: {
    DataSourceCard,
    LensAccountModal,
    MetaPlatformIcon,
    LensIcon,
    TikTokPlatformIcon
  },
  data () {
    return {
      loadingSources: true,
      platformToggle: 'meta',
      platformAccounts: {
        meta: [],
        tiktok: []
      },

      showLensEdit: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getLensLimit']),
    ...mapGetters('LensModule', ['getSidebarLenses']),
    lensCounts () {
      const open = this.getLensLimit - this.getSidebarLenses?.length || 0
      return {
        used: this.getSidebarLenses?.length || 0,
        open: Math.max(open, 0),
        total: this.getLensLimit || 0
      }
    },
    visiblePlatformAccounts () {
      return this.platformAccounts[this.platformToggle].map(account => ({
        ...account,
        ownerUser: this.teamMembers.find(member => member.id === account.createdBy) ?? null
      }))
    },
    platformOptions () {
      return [
        {
          name: 'Meta',
          icon: MetaPlatformIcon,
          action: () => this.platformToggle = 'meta',
          isActive: this.platformToggle === 'meta'
        },
        {
          name: 'TikTok',
          icon: TikTokPlatformIcon,
          action: () => this.platformToggle = 'tiktok',
          isActive: this.platformToggle === 'tiktok'
        }
      ]
    },
    formattedSelectedPlatformName () {
      return this.platformToggle === 'meta' ? 'Meta' : 'TikTok'
    }
  },
  async mounted () {
    await Promise.all([
      this.getTeamMembers(),
      this.getTeamPlatformAccounts()
    ])
    this.loadingSources = false
  },
  methods: {
    async getTeamMembers () {
      if (this.getTeam) {
        this.teamMembers = await FirebaseAPI.Teams.getTeamMembers()
      }
    },
    async getTeamPlatformAccounts () {
      this.loadingSources = true
      try {
        const { data: allProviders } = await LensAPI.Lens.getPlatformAccounts()
        for (const providerKey in this.platformAccounts) {
          const platformProviders = [...allProviders].filter(provider => provider.provider === providerKeyMap[providerKey])
          this.platformAccounts[providerKey] = platformProviders.map(provider => ({
            name: provider.business_name,
            id: provider.business_id,
            connected: provider.is_valid,
            email: provider.email,
            createdBy: provider.user_id,
            adAccounts: provider.advertisers ?? []
          }))
        }
      } catch (error) {
        console.error('Failed to get team platform accounts:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch platform accounts. See console for details.'
        })
      } finally {
        this.loadingSources = false
      }
    },
    // async getTeamFBConnections () {
    //   try {
    //     this.loadingSources = true
    //     const { data } = await lensAPI.Lens.getLensConnections()
    //     const allSources = data.user_connections

    //     for (const [platform, sources] of Object.entries(allSources)) {
    //       const platformConnections = sources.map((src) => ({
    //         ...src,
    //         created_by: this.teamMembers.find(member => member.id === src.connectionOwner) || { name: 'External user' }

    //       }))
    //       this.dataSources[platform] = platformConnections
    //     }
    //   } catch (error) {
    //     console.log(error)
    //   } finally {
    //     this.loadingSources = false
    //   }
    // },
  }
}
</script>

<style scoped>
.platform-toggle-btn {
  display: flex;
  gap: 2px;
  align-items: center;
  justify-content: center;
  width: max-content;
  padding: 6px 12px;
  border-radius: 6px;
  color: #5E6678;
  background-color: transparent;
  box-shadow: none;
  transition: background-color 100ms ease-in-out, color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.platform-toggle-btn:not(.active):hover {
  background-color: #ECEFF3;
  color: #303546;
}
.platform-toggle-btn.active {
  background-color: white;
  color: #303546;
  box-shadow: 0 36px 48px -12px rgba(53, 57, 75, 0.20), 0 24px 32px -12px rgba(53, 57, 75, 0.24), 0 1px 2px 0 rgba(162, 172, 186, 0.16);
}

.base-btn {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13),
    0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  border-radius: 6px;
  padding: 6px 8px 6px 8px;
}
.base-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17),
    0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.add-more-btn {
  box-shadow: 0 1px 2px 0 rgba(0, 56, 108, 0.08), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.add-more-btn:hover {
  box-shadow: none;
  background-color: #F6F8FA;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
